import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DynamicFormRenderer from './DynamicFormRenderer';
import { useFormPalette } from './DynamicFormPrimitives';
import { buildValidationSummary } from './validationSummary';
import { SkeletonField, fadeIn, fadeSlideUp, checkDraw, circleDraw, shake } from './PublicFormStyles';
import { styled, keyframes } from '@mui/material/styles';
import AuthenticatedUserChip from './AuthenticatedUserChip';

const DARK_GRADIENT =
  'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)';
const LIGHT_GRADIENT =
  'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 20%, #a5b4fc 40%, #818cf8 65%, #6366f1 85%, #4f46e5 100%)';

const COVER_HEIGHT_XS = 180;
const COVER_HEIGHT_SM = 240;
const COVER_HEIGHT_MD = 280;
const COVER_OVERLAP_XS = 140;
const COVER_OVERLAP_SM = 120;
const COVER_OVERLAP_MD = 100;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CoverImage = styled('div', {
  shouldForwardProp: (prop) => !['src', 'gradient'].includes(prop),
})(({ theme, src, gradient }) => ({
  width: '100%',
  height: `${COVER_HEIGHT_XS}px`,
  background: gradient || `url(${src}) center / cover no-repeat`,
  flexShrink: 0,
  [theme.breakpoints.up('sm')]: {
    height: `${COVER_HEIGHT_SM}px`,
  },
  [theme.breakpoints.up('md')]: {
    height: `${COVER_HEIGHT_MD}px`,
  },
}));

export default function PublicFormShell({
  loading,
  error,
  form,
  answers,
  validation,
  submitted,
  submitting,
  readOnly = false,
  uploadingFieldKey = null,
  headerRightSlot = null,
  stickyHeader = null,
  topBar = null,
  actions = null,
  statusBanner = null,
  authGate = null,
  coverImageUrl,
  isPreview = false,
  fillViewport = true,
  onChange,
  onFieldBlur,
  onUpload,
}) {
  const palette = useFormPalette();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hasCustomCover = coverImageUrl !== undefined && coverImageUrl !== '';
  const hasCover = true;
  const coverGradient = hasCustomCover ? null : (isDark ? DARK_GRADIENT : LIGHT_GRADIENT);
  const coverSrc = hasCustomCover ? coverImageUrl : null;
  const rootMinHeight = fillViewport ? '100vh' : 0;
  const compact = !fillViewport;

  const coverHeight = compact
    ? `${COVER_HEIGHT_XS}px`
    : { xs: `${COVER_HEIGHT_XS}px`, sm: `${COVER_HEIGHT_SM}px`, md: `${COVER_HEIGHT_MD}px` };

  const compactCardMargin = '12px';
  const coverContentSx = hasCover
    ? {
        mt: compact ? `-${COVER_OVERLAP_XS}px` : { xs: `-${COVER_OVERLAP_XS}px`, sm: `-${COVER_OVERLAP_SM}px`, md: `-${COVER_OVERLAP_MD}px` },
        mb: compact ? 4 : { xs: 4, sm: 6 },
        position: 'relative',
        bgcolor: palette.surface,
        borderRadius: '20px',
        border: `1px solid ${palette.border}`,
        px: compact ? 2.5 : { xs: 2.5, sm: 5, md: 6 },
        py: compact ? 3 : { xs: 4, sm: 6 },
        maxWidth: compact ? `calc(100% - ${compactCardMargin} * 2)` : 780,
        mx: compact ? 'auto' : { xs: 2, sm: 3, md: 'auto' },
      }
    : {
        maxWidth: compact ? `calc(100% - ${compactCardMargin} * 2)` : 680,
        mx: compact ? 'auto' : { xs: 2, sm: 3, md: 'auto' },
        py: 6,
        px: compact ? 2 : { xs: 2, sm: 4, md: 0 },
      };

  if (loading) {
    return (
      <Box sx={{ minHeight: rootMinHeight, bgcolor: palette.pageBg }}>
        {hasCover && (
          <Box sx={{ width: '100%', height: coverHeight, bgcolor: palette.chipBg, animation: `${fadeIn} 300ms ease` }} />
        )}
        <Box sx={{ ...coverContentSx, animation: `${fadeIn} 300ms ease` }}>
          <Box sx={{ mb: 5 }}>
            <Box sx={{ width: 200, height: 28, borderRadius: '6px', bgcolor: palette.chipBg, mb: 1.5, animation: `${SkeletonField} 1500ms ease-in-out infinite` }} />
            <Box sx={{ width: 300, height: 15, borderRadius: '4px', bgcolor: palette.chipBg }} />
          </Box>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonField key={i} palette={palette}>
              <div className="skel-label" />
              <div className="skel-input" />
            </SkeletonField>
          ))}
        </Box>
      </Box>
    );
  }

  if (authGate) return authGate;

  if (error && !form) {
    return (
      <Box
        sx={{
          minHeight: rootMinHeight,
          bgcolor: palette.pageBg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.5,
          px: 2,
          animation: `${fadeIn} 400ms ease`,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: palette.chipBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ fontSize: '24px', color: palette.textSecondary }}>!</Typography>
        </Box>
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: palette.textPrimary }}>
          Something went wrong
        </Typography>
        <Typography sx={{ fontSize: '15px', color: palette.textSecondary, textAlign: 'center', maxWidth: 400 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!form && !loading) {
    return (
      <Box
        sx={{
          minHeight: rootMinHeight,
          bgcolor: palette.pageBg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          animation: `${fadeIn} 400ms ease`,
        }}
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: palette.textPrimary }}>
          Form not found
        </Typography>
        <Typography sx={{ fontSize: '15px', color: palette.textSecondary }}>
          {error || 'This form may have been removed or the link is incorrect.'}
        </Typography>
      </Box>
    );
  }

  const coverElement = compact
    ? <Box sx={{ width: '100%', height: `${COVER_HEIGHT_XS}px`, background: coverGradient || `url(${coverSrc}) center / cover no-repeat`, flexShrink: 0, animation: `${fadeIn} 400ms ease` }} />
    : <CoverImage src={coverSrc} gradient={coverGradient} sx={{ animation: `${fadeIn} 400ms ease` }} />;

  if (submitted) {
    return (
      <Box sx={{ minHeight: rootMinHeight, bgcolor: palette.pageBg }}>
        {hasCover && coverElement}
        <Box
          sx={{
            ...coverContentSx,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 10,
            animation: `${fadeIn} 400ms ease`,
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 56 56"
            fill="none"
            sx={{ width: 56, height: 56, mb: 1, '& .circle-draw': { animation: `${circleDraw} 600ms ease-in-out 200ms forwards` }, '& .check-draw': { animation: `${checkDraw} 400ms ease-in-out 600ms forwards` } }}
          >
            <circle cx="28" cy="28" r="27" stroke={palette.accent} strokeWidth="2" opacity="0.15" />
            <circle
              className="circle-draw"
              cx="28" cy="28" r="27"
              stroke={palette.accent}
              strokeWidth="2"
              strokeDasharray="170"
              strokeDashoffset="170"
            />
            <path
              className="check-draw"
              d="M18 28L25 35L38 22"
              stroke={palette.accent}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              strokeDasharray="30"
              strokeDashoffset="30"
            />
          </Box>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 500,
              color: palette.textPrimary,
              animation: `${fadeSlideUp} 400ms ease-out 400ms both`,
            }}
          >
            Thank you
          </Typography>
          <Typography
            sx={{
              fontSize: '15px',
              color: palette.textSecondary,
              animation: `${fadeSlideUp} 400ms ease-out 550ms both`,
            }}
          >
            Your submission has been received.
          </Typography>
        </Box>
      </Box>
    );
  }

  const validationSummary = buildValidationSummary(form, validation);
  const hasValidationFeedback =
    validationSummary.errors.length > 0 || validationSummary.warnings.length > 0;

  return (
    <Box sx={{ minHeight: rootMinHeight, bgcolor: palette.pageBg, position: 'relative' }}>
      {/* When a topBar is slotted (e.g. FormFlow), the consumer is responsible for
          rendering the user chip inline inside that band so it doesn't float over
          and overlap the sticky header. Standalone forms with no topBar keep the
          fixed top-right chip. */}
      {!topBar && <AuthenticatedUserChip />}
      {topBar && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 11,
            bgcolor: palette.surface,
            borderBottom: `1px solid ${palette.border}`,
            backdropFilter: 'blur(8px)',
          }}
        >
          {topBar}
        </Box>
      )}
      {hasCover && coverElement}
      <Box sx={coverContentSx}>
        {stickyHeader && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              mx: compact ? -2.5 : { xs: -2.5, sm: -5, md: -6 },
              mt: hasCover ? (compact ? -4 : { xs: -4, sm: -6, md: -6 }) : 0,
              px: compact ? 2.5 : { xs: 2.5, sm: 5, md: 6 },
              py: 1.5,
              mb: 2,
              bgcolor: palette.surface,
              borderBottom: `1px solid ${palette.border}`,
              borderRadius: hasCover ? '20px 20px 0 0' : 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              backdropFilter: 'blur(8px)',
            }}
          >
            {stickyHeader}
          </Box>
        )}

        {isPreview && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1,
              px: 1.5,
              mb: 3,
              bgcolor: '#fff3e0',
              borderRadius: '10px',
              border: '1px solid #ffcc80',
            }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#e65100' }} />
            <Typography sx={{ fontSize: '13px', color: '#e65100', fontWeight: 600 }}>
              Preview — You are viewing the draft version. Submissions will trigger a test run.
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 5 }}>
          <Typography
            sx={{
              fontSize: '28px',
              fontWeight: 500,
              color: palette.textPrimary,
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            {form?.title || 'Form'}
          </Typography>
          {form?.description && (
            <Typography sx={{ fontSize: '15px', color: palette.textSecondary, lineHeight: 1.6 }}>
              {form.description}
            </Typography>
          )}
          {headerRightSlot && <Box sx={{ mt: 2 }}>{headerRightSlot}</Box>}
        </Box>

        {error && (
          <Typography sx={{ fontSize: '13px', color: palette.errorText, mb: 2 }}>
            {error}
          </Typography>
        )}

        {hasValidationFeedback && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: '10px',
              bgcolor: palette.chipBg,
              border: `1px solid ${palette.chipBorder}`,
              animation: `${slideDown} 250ms ease-out`,
            }}
          >
            {validationSummary.errors.length > 0 && (
              <Box sx={{ mb: validationSummary.warnings.length > 0 ? 1.5 : 0 }}>
                <Typography sx={{ fontSize: '13px', color: palette.errorText, fontWeight: 600, mb: 0.75 }}>
                  Please fix these fields:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {validationSummary.errors.map((item) => (
                    <Typography
                      key={`error-${item.key}`}
                      component="li"
                      onClick={() => {
                        const el = document.getElementById(`field-${item.key}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      sx={{
                        fontSize: '13px',
                        color: palette.errorText,
                        lineHeight: 1.5,
                        cursor: 'pointer',
                        '& span': { textDecoration: 'underline', color: palette.textPrimary },
                        '&:hover span': { opacity: 0.7 },
                      }}
                    >
                      <span>{item.label}</span>: {item.message}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            {validationSummary.warnings.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '13px', color: palette.warningText, fontWeight: 600, mb: 0.75 }}>
                  Recommended updates:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {validationSummary.warnings.map((item) => (
                    <Typography
                      key={`warning-${item.key}`}
                      component="li"
                      onClick={() => {
                        const el = document.getElementById(`field-${item.key}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      sx={{
                        fontSize: '13px',
                        color: palette.warningText,
                        lineHeight: 1.5,
                        cursor: 'pointer',
                        '& span': { textDecoration: 'underline', color: palette.textPrimary },
                      }}
                    >
                      <span>{item.label}</span>: {item.message}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <DynamicFormRenderer
          form={form}
          answers={answers}
          onChange={onChange}
          onFieldBlur={onFieldBlur}
          validation={validation}
          readOnly={readOnly || submitting}
          onUpload={onUpload}
          uploadingFieldKey={uploadingFieldKey}
          publicMode
        />

        {statusBanner && (
          <Box sx={{ mt: 4 }}>{statusBanner}</Box>
        )}

        {actions && !statusBanner && (
          <Box sx={{ mt: 4 }}>
            {actions}
          </Box>
        )}

      </Box>
    </Box>
  );
}
